/* The presentation model for the ITSM integrations screen. Pure functions, no React, no fetch.
 *
 * **Three things on this screen are statements about a customer's incident response, so they are
 * derived here and tested.** Each one is a claim no single row can make and the API cannot make at
 * all, because every row is valid in isolation — which is the shape that makes the failure silent.
 *
 * `declared` is the first, and it reads the opposite way round to `alert-routing`'s. There, an
 * undeclared policy still mails critical incidents, so `declared: false` means "the platform's
 * default is running". Here the default is **no tickets at all**, so `declared: false` means nothing
 * reaches a service desk and nobody has chosen that — a different sentence, on a screen that looks
 * the same.
 *
 * `reachOf` is the second. A rule can be enabled, well-formed, stored, and file nothing: its
 * destination may be switched off, or deleted out from under it. The rule is valid, the connection
 * is valid, and the pair is dead. Nothing but a cross-reference can see it.
 *
 * `attentionOf` is the third and the narrowest: which connections an operator has to do something
 * about, separated from the ones somebody deliberately turned off. Colouring a disabled connection
 * as a fault would argue with a decision; leaving an unreachable one neutral would hide one.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type {
  ConnectionState,
  IncidentKind,
  ItsmConnection,
  Severity,
  TicketJobState,
  TicketPolicy,
  TicketRule,
} from '@/shared/api/generated/itsm';

/** Severity as an order, mirroring the backend's `_SEVERITY_RANK`.
 *
 * Declared rather than derived from the string, for the reason the backend states: alphabetically
 * 'critical' < 'info' < 'warning', so a text comparison would order a blocker first by luck and be
 * wrong for every other pair. Total via `satisfies`, so a fourth member is a compile error here
 * rather than a floor that silently admits it.
 */
const SEVERITY_RANK = {
  info: 0,
  warning: 1,
  critical: 2,
} satisfies Record<Severity, number>;

/** Whether a rule would file a ticket for an incident of this severity. Kind is a separate question. */
export function admitsSeverity(rule: TicketRule, severity: Severity): boolean {
  if (!rule.enabled) return false;
  return SEVERITY_RANK[severity] >= SEVERITY_RANK[rule.min_severity];
}

/** Whether a rule would file a ticket for an incident of this kind. `null` on the rule means every kind. */
export function admitsKind(rule: TicketRule, kind: IncidentKind): boolean {
  return rule.incident_kind === null || rule.incident_kind === kind;
}

/** The badge vocabulary, aliased from the UI kit rather than re-declared.
 *
 * **A local `'bad'` where the kit says `'danger'` is the two-vocabularies failure in miniature**,
 * and it is worse here than it sounds: the screen would need a map from one to the other, the map
 * would need a fallback for a member it did not know, and a fallback in a tone map is exactly how
 * §9's `verdictTone` stripped a failed row of its red badge. Aliasing means there is nothing to map
 * and a member added to the kit is available here for free.
 *
 * `alert-routing` declares its own four-member subset, which is safe only because every member it
 * names is spelled the same way. This file needs `danger`, so the alias is the honest form.
 */
export type Tone = BadgeTone;

/* **Total maps via `satisfies`, never a `switch` with a `default:`.** §9 records `verdictTone` in
 * `core.ai.evals` taking the *permitted* tone for an unrecognised verdict, with `tsc` silent — so a
 * failed row would have lost its red badge. A total map makes an added member a compile error in
 * this file instead.
 */

const STATE_TONE = {
  registered: 'neutral',
  verified: 'ok',
  unreachable: 'warn',
  disabled: 'neutral',
} satisfies Record<ConnectionState, Tone>;

/** The tone for a connection's state.
 *
 * **`disabled` is `neutral` and `unreachable` is `warn`, and the asymmetry is the whole point.**
 * Disabled is somebody's decision — a maintenance window, a desk being migrated — and painting it
 * as a fault argues with a colleague who did it on purpose. Unreachable is a reading: a credential
 * rotated, a network rule changed, and nobody has been told. The queue keeps retrying an unreachable
 * connection precisely because it may recover, so this is amber rather than red.
 *
 * `registered` is neutral too — never contacted is the normal state of a connection an operator
 * created thirty seconds ago, and amber there would greet every new integration with a warning.
 */
export function stateTone(state: ConnectionState): Tone {
  return STATE_TONE[state];
}

/** Whether the ticket queue may use this connection at all.
 *
 * Mirrors `ItsmConnectionState.in_service` on the backend, including its non-obvious half: an
 * **unreachable connection is still in service**. A verification that failed an hour ago is evidence
 * about an hour ago — a credential may have been rotated since — and retrying is how a connection
 * recovers. Only a human's `disabled` stops it, because that is a statement about the future rather
 * than a reading of the past.
 *
 * Duplicated from the backend deliberately and pinned by a test, because the screen has to say which
 * rules are dead *before* the next incident proves it, and a round trip cannot answer a question
 * about a rule that has not fired yet.
 */
export function inService(connection: ItsmConnection): boolean {
  return connection.state !== 'disabled';
}

export type Attention = 'none' | 'never-verified' | 'unreachable' | 'switched-off';

/** What, if anything, an operator has to do about this connection.
 *
 * Four states rather than a boolean, because the three non-`none` ones want three different
 * sentences and only two of them are problems.
 *
 * `never-verified` is a connection registered and never contacted. Registration deliberately does
 * not call the provider — a form that blocked on a network round trip would fail for a firewall rule
 * and look like a wrong password — so this is the ordinary state of a new integration and the prompt
 * is "check it works", not "something is wrong".
 *
 * `switched-off` is reported rather than hidden, which is the half a filter would lose. A tenant
 * whose only ServiceNow connection is disabled has rules that file nothing, and a screen that
 * quietly omitted it would make an estate with no ticketing look like an estate with none
 * configured.
 */
export function attentionOf(connection: ItsmConnection): Attention {
  if (connection.state === 'disabled') return 'switched-off';
  if (connection.state === 'unreachable') return 'unreachable';
  if (connection.last_checked_at === null) return 'never-verified';
  return 'none';
}

export type Reach = 'files' | 'destination-off' | 'destination-missing' | 'rule-off';

/** Whether a rule would actually produce a ticket, given the connections that exist.
 *
 * **The cross-reference this screen exists for.** Every rule is valid on its own and every
 * connection is valid on its own; only the pair can be dead, and three of the four ways it dies are
 * invisible from either table alone.
 *
 * `destination-missing` is separated from `destination-off` because they are different accidents.
 * A missing destination means the rule outlived its connection — which the API should prevent by
 * withdrawing the rules with the connection, so seeing one is a sign the two lists are out of step
 * rather than a configuration mistake. A switched-off destination is somebody's maintenance window,
 * and the rule starts working again when they are finished.
 *
 * `rule-off` last, because a disabled rule pointing at a disabled connection is primarily a disabled
 * rule: the thing to tell somebody is the decision they made, not a consequence of it.
 */
export function reachOf(rule: TicketRule, connections: readonly ItsmConnection[]): Reach {
  if (!rule.enabled) return 'rule-off';
  const destination = connections.find(
    (candidate) => candidate.id === rule.connection_id,
  );
  if (destination === undefined) return 'destination-missing';
  if (!inService(destination)) return 'destination-off';
  return 'files';
}

/** The health of a destination a rule *does* file into — `null` when there is nothing to say.
 *
 * **The hole in the map's own argument, found by rendering it.** `reachOf` answers whether a rule
 * would produce a ticket, and it treats an unreachable destination as live on purpose: `inService`
 * argues that a verification which failed an hour ago is evidence about an hour ago, and that
 * retrying is how a connection recovers. That reasoning is right and it leaves one pairing reported
 * as unconditionally fine — a live rule aimed at a desk whose credential may have been rotated,
 * rendered with the same confident solid track as a desk that answered a minute ago.
 *
 * It is the pairing this screen exists for, arriving late: valid rule, in-service connection, and
 * the failure shows up at the next incident rather than now. The desk card below carries the amber
 * reading, but a reader scanning the map — which is the thing built to answer "which of these are
 * dead" — would not look down.
 *
 * So the *track* keeps saying the rule files, because it does, and the **destination** carries the
 * doubt. Two different questions, answered in the two places that own them, rather than a third
 * track state that would collapse the distinction `inService` was written to draw.
 *
 * `switched-off` can never be returned: it implies `!inService`, which `reachOf` has already called
 * `destination-off`, so the track has said it. Returning it here would print the same fact twice in
 * one row, in two different voices.
 */
export function destinationWarningOf(
  rule: TicketRule,
  connections: readonly ItsmConnection[],
): Exclude<Attention, 'none' | 'switched-off'> | null {
  if (reachOf(rule, connections) !== 'files') return null;
  const destination = connections.find(
    (candidate) => candidate.id === rule.connection_id,
  );
  if (destination === undefined) return null;
  const attention = attentionOf(destination);
  return attention === 'unreachable' || attention === 'never-verified' ? attention : null;
}

export type PolicyState =
  'undeclared' | 'silenced' | 'files' | 'files-partly' | 'files-nowhere';

export interface PolicySummary {
  readonly state: PolicyState;
  readonly tone: Tone;
  /** How many rules would actually produce a ticket. Zero is the interesting number. */
  readonly living: number;
  /** How many are declared but reach nothing — the figure that makes `files-nowhere` actionable. */
  readonly dead: number;
}

/** The headline: does anything at all reach a service desk, and is that a choice?
 *
 * **Four states, each a different conversation**, and the first two are the pair a single boolean
 * would collapse.
 *
 * `undeclared` means nobody has chosen. The default here is no tickets, which is what every install
 * did before this module existed — so this is not a misconfiguration, it is an unanswered question,
 * and a screen that called it a problem would be nagging about a feature somebody has not adopted.
 *
 * `silenced` means a tenant declared a policy and then withdrew every rule. That *is* a decision,
 * and reading it as "nobody has chosen" would lose it.
 *
 * `files-nowhere` is the dangerous one: rules exist, they look healthy in a list, and every one of
 * them points at a destination that is off or gone. The tenant believes they have ticketing.
 */
export function summarise(
  policy: TicketPolicy,
  connections: readonly ItsmConnection[],
): PolicySummary {
  const living = policy.rules.filter(
    (rule) => reachOf(rule, connections) === 'files',
  ).length;
  const dead = policy.rules.length - living;

  if (!policy.declared && policy.rules.length === 0) {
    return { state: 'undeclared', tone: 'neutral', living, dead };
  }
  if (policy.rules.length === 0) {
    return { state: 'silenced', tone: 'neutral', living, dead };
  }
  if (living === 0) {
    return { state: 'files-nowhere', tone: 'danger', living, dead };
  }
  /* **`files` used to cover this, and that was the headline reassuring somebody whose rules were
   * broken.** One live rule beside three dead ones read "Incidents reach your service desk" in the
   * ok tone — true of the one and silent about the three, at the top of the screen, in the largest
   * type on it. Every dead row says so individually and none of them says it as a whole, which is
   * the job a headline has.
   *
   * `core.observability`'s alert-routing summary already models exactly this as `partial`; this
   * module had four states where its sibling has five. Warn rather than danger, because tickets
   * *are* being raised — the distinction from `files-nowhere`, where none are. */
  if (dead > 0) {
    return { state: 'files-partly', tone: 'warn', living, dead };
  }
  return { state: 'files', tone: 'ok', living, dead };
}

/** Which kinds a critical incident would file no ticket for. Empty when every kind is covered.
 *
 * Per kind rather than one boolean, for `uncoveredKinds`' reason one screen over: "we raise tickets
 * for schema drift and not for a contract breach" is the realistic shape of a partly-declared
 * policy, and a single flag would report the whole estate as covered on the strength of one rule.
 *
 * **Reach is part of the question**, which is the difference from the alert-routing version: a rule
 * whose destination is switched off covers nothing, however well it matches.
 *
 * **This readout is about gaps the rules table *hides*, which is why no rules means nothing to
 * report.** The word doing the work in "which kinds are not covered" is *silently*: a tenant with
 * eight healthy-looking rules and a ninth kind nobody matched cannot see that from any row, and
 * that is the whole reason to derive it. A tenant with no rules at all is told so in one sentence
 * at the top of the screen, in `summarise`'s own words — so listing ten kinds underneath would be
 * restating the headline ten times in amber, for a tenant who either turned ticketing off on
 * purpose or has not adopted it yet. Neither is a finding, and both are already stated.
 */
export function uncoveredKinds(
  policy: TicketPolicy,
  connections: readonly ItsmConnection[],
  kinds: readonly IncidentKind[],
): readonly IncidentKind[] {
  /* Declared or not: with no rules there is no table to hide a gap, so the headline is the complete
   * answer and this has nothing to add. The first draft keyed this on `declared` — which read as a
   * meaningful distinction and was not, because `summarise` already separates `silenced` from
   * `undeclared` and this function would have rendered the same ten chips under one of them. */
  if (policy.rules.length === 0) return [];
  return kinds.filter(
    (kind) =>
      !policy.rules.some(
        (rule) =>
          admitsSeverity(rule, 'critical') &&
          admitsKind(rule, kind) &&
          reachOf(rule, connections) === 'files',
      ),
  );
}

const JOB_TONE = {
  pending: 'info',
  running: 'info',
  succeeded: 'ok',
  failed: 'danger',
  retired: 'neutral',
} satisfies Record<TicketJobState, Tone>;

/** The tone for one ticket-creation attempt.
 *
 * **`retired` is `neutral`, not `bad`**, mirroring the backend's reason for having the state at
 * all: a job whose connection was deleted or disabled while it waited has nothing wrong with it.
 * Painting it red puts a fault on a screen about something a colleague did on purpose, and sends
 * somebody looking for a provider outage that never happened.
 */
export function jobTone(state: TicketJobState): Tone {
  return JOB_TONE[state];
}

/** A stable React key for a rule.
 *
 * The rule id alone, because the server mints one per rule and migration 0113's unique index makes
 * `(connection, kind, floor)` unique anyway. Composing a key out of the fields would reintroduce the
 * `unique on ["a.b"]` collision §9 records, where two different rules merged into one row because
 * the separator appeared inside a value.
 */
export function keyOf(rule: TicketRule): string {
  return rule.id;
}
